import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import LiveSupport from './components/LiveSupport';
import ProtectedRoute from './components/ProtectedRoute';
import { SettingsProvider } from './contexts/SettingsContext';
import { AuthProvider } from './contexts/AuthContext';

// Loading fallback
const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
    <p className="ml-3">Loading...</p>
  </div>
);

// Eagerly load Home component to improve initial load experience
import Home from './pages/Home';

// Lazy load other pages to reduce initial bundle size
const About = lazy(() => import('./pages/About'));
const SubmitTicket = lazy(() => import('./pages/SubmitTicket'));
const Contact = lazy(() => import('./pages/Contact'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
const PasswordReset = lazy(() => import('./pages/PasswordReset'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const KnowledgeBase = lazy(() => import('./pages/KnowledgeBase'));
const Gallery = lazy(() => import('./pages/Gallery'));
const TicketSubmitted = lazy(() => import('./pages/TicketSubmitted'));
const BlogManager = lazy(() => import('./pages/BlogManager'));
const CustomerFeedback = lazy(() => import('./pages/CustomerFeedback'));

const PublicLayout = () => (
  <div className="min-h-screen flex flex-col">
    <Navbar />
    <main className="flex-grow">
      <Suspense fallback={<LoadingFallback />}>
        <Outlet />
      </Suspense>
    </main>
    <Footer />
    <LiveSupport />
  </div>
);

const AdminLayout = () => (
  <div className="min-h-screen flex flex-col">
    <Suspense fallback={<LoadingFallback />}>
      <Outlet />
    </Suspense>
  </div>
);

const App: React.FC = () => {
  return (
    <AuthProvider>
      <SettingsProvider>
        <Router>
          <Routes>
            {/* Public routes */}
            <Route element={<PublicLayout />}>
              <Route index element={<Home />} />
              <Route path="about" element={<About />} />
              <Route path="submit-ticket" element={<SubmitTicket />} />
              <Route path="ticket-submitted" element={<TicketSubmitted />} />
              <Route path="contact" element={<Contact />} />
              <Route path="privacy" element={<PrivacyPolicy />} />
              <Route path="refund-policy" element={<RefundPolicy />} />
              <Route path="knowledge-base" element={<KnowledgeBase />} />
              <Route path="gallery" element={<Gallery />} />
              <Route path="feedback" element={<CustomerFeedback />} />
            </Route>

            {/* Admin routes */}
            <Route element={<AdminLayout />}>
              <Route path="admin-login" element={<AdminLogin />} />
              <Route path="password-reset" element={<PasswordReset />} />
              <Route
                path="admin/*"
                element={
                  <ProtectedRoute>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="blog-manager"
                element={
                  <ProtectedRoute>
                    <BlogManager />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" />
        </Router>
      </SettingsProvider>
    </AuthProvider>
  );
};

export default App;